import { Box } from '@chakra-ui/react';
import { HUBSPOT_TICKET_CONVERSION_PAGE_FIELD } from 'lib/constants';
import React, { FunctionComponent, useState } from 'react';
import { primary } from 'themes/components/button';
import {
  FormHeroWithAssetData,
  FormHeroWithTextData,
  FormModalData,
  InbuiltFormData,
  StandaloneFormData,
} from 'utils/types/formData';

import { Form } from './Form';

interface FormWrapperProps {
  data: FormHeroWithAssetData | FormModalData | FormHeroWithTextData | StandaloneFormData | InbuiltFormData;
  onSubmit?: () => void;
  onReady?: () => void;
  style?: any;
}

export const FormWrapper: FunctionComponent<FormWrapperProps> = ({ data, onSubmit, onReady, style }) => {
  const [hasBeenSubmitted, setHasBeenSubmitted] = useState<boolean>(false);

  // Determine if background is dark (darkblue)
  const isDarkBackground =
    'backgroundColor' in data &&
    (data.backgroundColor?.includes('darkblue') ||
      data.backgroundColor?.includes('#112C49') ||
      data.backgroundColor?.includes('--Webinar'));

  // Use mountain.80 for normal backgrounds, mountain.40 for dark backgrounds
  const consentTextColor =
    'consentTextColorProp' in data && data.consentTextColorProp
      ? data.consentTextColorProp
      : isDarkBackground
      ? 'mountain.40'
      : 'mountain.70';

  // For input fields: always use dark text since input fields have light backgrounds
  const inputTextColor = 'fieldTextColor' in data && data.fieldTextColor ? data.fieldTextColor : '#273943';
  const dropdownArrowColor = '%23273943';

  return (
    <Box
      {...style}
      color={isDarkBackground ? 'white' : 'mountain'}
      sx={{
        form: {
          display: hasBeenSubmitted ? 'none' : 'flex',
          flexDir: 'column',
        },
        'form fieldset': {
          display: 'flex',
          flexDir: { base: 'column', md: 'row' },
          gap: '25px',
          '@media (max-width: 639px)': {
            gap: '0px',
          },
        },
        'form fieldset div': {
          display: 'auto',
          flexDir: 'column',
          marginTop: '5px',
        },
        'form fieldset div label': {
          mb: 'xxs',
        },
        'div ul': {
          listStyleType: 'none',
        },
        'label span': {
          textStyle: 'mobile.body.s',
          fontWeight: 400,
          fontSize: '15px',
          lineHeight: '20px',
        },
        'div p span': {
          textStyle: 'mobile.body.s',
          fontSize: '15px',
          lineHeight: '20px',
        },
        'h1, h2, h3, h4, h5, h6': {
          textStyle: 'desktop.body.l',
        },
        hr: {
          mb: '3xs',
          mt: 'xs',
        },
        'input:-webkit-autofill': {
          WebkitBoxShadow: '0 0 0 100px transparent inset',
          WebkitTextFillColor: inputTextColor,
        },
        'input, textarea, select': {
          textStyle: 'desktop.body.s',
          p: 'xxs',
          bg: data.fieldColor,
          color: `${inputTextColor} !important`,
          WebkitTextFillColor: `${inputTextColor} !important`,
          borderWidth: { base: '1px', md: '2px' },
          '@media (min-width: 48em)': {
            borderWidth: '2px',
          },
          borderColor: 'mountain.20',
          _placeholder: {
            color:
              'consentTextColorProp' in data && data.consentTextColorProp ? data.consentTextColorProp : 'mountain.60',
            fontSize: '15px',
            lineHeight: '20px',
          },
          _focus: {
            borderColor: 'cyan.web',
            outline: 'none',
          },
          _hover: {
            outline: 'none',
            _placeholder: {
              color:
                'consentTextColorProp' in data && data.consentTextColorProp
                  ? data.consentTextColorProp
                  : 'mountain.base',
              fontSize: '15px',
              lineHeight: '20px',
            },
          },
        },
        'form fieldset div, select, input, textarea': {
          w: '100%',
        },
        textarea: {
          h: '100px',
        },
        'input[type=submit]': {
          w: { base: '100%', md: 'fit-content' },
          ...primary,
          color: 'white !important',
          WebkitTextFillColor: 'white !important',
          borderWidth: '0px',
          _hover: {
            bg: 'cyan.a11y',
            color: 'white !important',
          },
          cursor: 'pointer',
          fontFamily: 'Nunito',
          fontStyle: 'normal',
          fontWeight: 350,
          fontSize: '18px',
          lineHeight: '20px',
          borderRadius: '4px',
          p: '20px',
        },
        'input[type=submit]:disabled': {
          bg: '#C0C0C0 !important',
          color: '#6B6B6B !important',
          WebkitTextFillColor: '#6B6B6B !important',
          cursor: 'not-allowed !important',
        },
        a: {
          color: 'cyan.web',
          _hover: {
            color: 'cyan.a11y',
            textDecor: 'underline',
          },
          _visited: {
            color: 'cyan.a11y',
          },
          _focus: {
            p: '3xs',
            borderColor: 'cyan.web',
            borderWidth: '1px',
            outline: 'none',
          },
        },
        div: {
          h: '100%',
        },
        '.submitted-message': {
          display: 'flex',
          flexDir: 'column',
          gap: 'xs',
          textAlign: data.submittedMessageAlignment,
          w: '100%',
          color: 'thankYouBackgroundImage' in data && data.thankYouBackgroundImage ? 'white' : '',
        },
        '.submitted-message p:first-of-type': {
          textStyle: 'desktop.h4',
        },
        '.submitted-message p:first-of-type span': {
          textStyle: 'desktop.h4',
          color: 'thankYouBackgroundImage' in data && data.thankYouBackgroundImage ? 'white !important' : '',
        },
        '.submitted-message a': {
          color: 'cyan.web',
          fontSize: '18px',
          textDecoration: 'underline',
          fontWeight: 'bold',
          _hover: {
            color: 'cyan.a11y',
          },
        },
        '.submitted-message p': {
          minH: '60px',
        },
        'select::-ms-expand': {
          display: 'none',
        },
        select: {
          WebkitAppearance: 'none',
          MozAppearance: 'none',
          appearance: 'none',
          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' fill='none'%3E%3Cpath stroke='${dropdownArrowColor}' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 9 6 6 6-6'/%3E%3C/svg%3E")`,
          backgroundRepeat: 'no-repeat',
          backgroundPositionX: '98%',
          backgroundPositionY: '10px',
          fontSize: '15px',
          lineHeight: '20px',
        },
        'select option': {
          color: '#273943',
          backgroundColor: '#FFF',
          fontSize: '15px',
          lineHeight: '20px',
        },
        '.hs-error-msgs': {
          pt: '10px',
          mt: '5px',
          color: 'field',
          fontFamily: 'Nunito',
          fontStyle: 'normal',
          fontWeight: 325,
          fontSize: '14px',
          lineHeight: '18px',
        },
        '.hs-error-msg, .hs-error-msgs': {
          pt: '10px',
          color: 'field',
          fontFamily: 'Nunito',
          fontStyle: 'normal',
          fontWeight: 325,
          fontSize: '14px',
          lineHeight: '18px',
        },
        '.error': {
          borderColor: 'field',
        },
        '.legal-consent-container': {
          display: 'flex',
          flexDirection: 'column',
          paddingTop: { base: '10px', md: '20px' },
          color: 'consentTextColorProp' in data && data.consentTextColorProp ? data.consentTextColorProp : '#68747B',
          fontSize: '15px',
          fontFamily: 'Nunito',
          fontWeight: 400,
          lineHeight: '20px',
          letterSpacing: '0.15px',
          wordWrap: 'break-word',
          marginBottom: '20px',
        },
        '.legal-consent-container a': {
          color: 'consentTextColorProp' in data && data.consentTextColorProp ? data.consentTextColorProp : 'unset',
          textDecoration: 'underline',
        },
        '.legal-consent-container li.hs-form-booleancheckbox:not(:last-child)': {
          marginBottom: '0px',
        },
        '.legal-consent-container li.hs-form-booleancheckbox > label': {
          marginBottom: '0px',
        },
        '.hs-form-booleancheckbox-display': {
          display: 'flex',
          flexDirection: 'row',
          gap: 'xxs',
          alignItems: 'center',
          borderWidth: '2px',
          borderColor: 'transparent',
          p: '3xs',
          _focusWithin: {
            borderColor: 'cyan.a11y',
          },
        },
        '.hs-form-booleancheckbox-display input': {
          w: '20px',
          h: '20px',
        },
        '.hs-form-booleancheckbox label span': {
          color: 'consentTextColorProp' in data && data.consentTextColorProp ? data.consentTextColorProp : '#68747B',
          fontSize: '15px',
          fontFamily: 'Nunito',
          fontWeight: 400,
          lineHeight: '20px',
          letterSpacing: '0.15px',
          wordWrap: 'break-word',
          display: 'flex',
          flexDirection: 'row',
        },
        'li.hs-form-booleancheckbox > label': {
          alignItems: 'flex-start',
        },
        '.inputs-list li': {
          display: 'flex',
          alignItems: 'center',
          marginBottom: '10px',
        },
        '.hs-form-checkbox-display': {
          display: 'flex',
          alignItems: 'center',
          gap: '10px' /* Space between checkbox and label text */,
          color: consentTextColor,
        },
        '.inputs-list': {
          listStyle: 'none',
          padding: 0,
          margin: 0,
        },
        '.input': {
          marginTop: '10px',
        },
        fieldset: {
          border: 'none',
          padding: 0,
          margin: 0,
        },
        '.hs-form-checkbox-display input[type="checkbox"]': {
          width: '20px',
          height: '20px',
          margin: 0, // Prevent extra default margins
        },
        '#label-personalisation_interest-7e99dd5b-ff3f-44c5-b151-f62a93554739': {
          display: 'block',
          marginTop: '30px',
        },
      }}
    >
      <Form
        portalId={data.portalId}
        formId={data.formId}
        onReady={() => {
          const fields = document.querySelectorAll('input');
          fields.forEach((field) => {
            const inputField = field as HTMLInputElement;
            if (inputField.name === HUBSPOT_TICKET_CONVERSION_PAGE_FIELD) {
              inputField.value = window.location.href;
            }
          });

          if (onReady) {
            onReady();
          }
        }}
        onSubmit={() => {
          if (onSubmit) {
            onSubmit();
          }
          setHasBeenSubmitted(true);
        }}
      />
    </Box>
  );
};
